import {
  useConnectionGetConnectionQuery,
  useEmployeeGetCalculationsQuery,
  useEmployeeGetEmploymentsQuery,
  useEmployeeGetParticipationsQuery,
  useSnakeBar,
} from '../../../../../../hooks';
import { useParams } from 'react-router-dom';
import React, { Fragment } from 'react';
import { format } from 'date-fns';
import * as Yup from 'yup';
import { TranslationKeys } from '../../../../../../translations';
import { Grid } from '@mui/material';
import { InputFieldFactory } from '../../../../../../components';
import {
  EmployeePensionIncomeDetailsPanel,
  EmployeePensionNotRegularMsg,
  EmployeePensionPersonalDetailsPanel,
} from '../EmployeePensionTab/components';
import {
  EmployeeIncomePremiumSickLeavePanel,
  EmployeeIncomePremiumsWGA,
  EmployeeIncomePremiumsWGAERD,
  EmployeeIncomePremiumsWIA,
} from './components';
import { useEmployeeUpdateEmployeeCalculationsMutation } from '../../../../../../hooks/api/employee/useEmployeeUpdateEmployeeCalculationsMutation';
import { ParticipationTypeEnum } from 'app/types';

const ONE_SECOND_DEBOUNCE_TIME = 1000;

const PARTICIPATION_ALLOWED_PRODUCT_IDS = [
  '3c0d165e-8949-4332-8939-76497d030881',
  '36e2998c-18a9-4375-a382-26a799492a69',
  'b2979d93-2881-4727-a5cb-f056167f0bf4',
  '7db134bc-119d-4011-9f8b-6e419f0797b1',
] as const;

export const EmployeeIncomeTab = () => {
  const { employerId, employeeId } = useParams();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  const defaultDateFilterValue = React.useMemo(() => format(Date.now(), 'yyyy-MM-dd'), []);
  const [dateFilterValue, setDateFilterValue] = React.useState<string>(defaultDateFilterValue);

  const { mutate: updateEmployeeCalculations } = useEmployeeUpdateEmployeeCalculationsMutation();

  const onChangeCalculations = React.useCallback(
    field => {
      const method = 'updateEmployeeCalculations';

      if (employeeId && employerId)
        updateEmployeeCalculations(
          {
            employerId,
            employeeId,
            field: field.name,
            value: field.value,
          },
          {
            onSuccess: () => {
              showSuccessSnakeBar({ method });
            },
            onError: () => {
              showErrorSnakeBar({ method });
            },
          },
        );
    },
    [employeeId, employerId, showErrorSnakeBar, showSuccessSnakeBar, updateEmployeeCalculations],
  );

  const {
    data,
    isLoading: isLoadingEmployeeCalculationsQuery,
    isFetching: isFetchingEmployeeCalculationsQuery,
  } = useEmployeeGetCalculationsQuery({
    variables: {
      employerId,
      employeeId,
      date: dateFilterValue,
    },
    options: {
      enabled: !!(employerId && employeeId && dateFilterValue),
    },
  });

  const isEmployeeCalculationsLoading = isLoadingEmployeeCalculationsQuery || isFetchingEmployeeCalculationsQuery;

  const { data: employmentsData } = useEmployeeGetEmploymentsQuery({
    variables: { employerId, employeeId },
    options: { enabled: !!(employerId && employeeId) },
  });

  const connectionId = employmentsData?.data?.[0]?.connectionId;

  const { data: connectionData } = useConnectionGetConnectionQuery({
    variables: { connectionId },
    options: { enabled: !!connectionId },
  });

  const showParticipations = React.useMemo(() => {
    const productId = connectionData?.product?.productId;
    const relatedProductIds = connectionData?.relatedProductIds?.map(p => p) ?? [];

    return PARTICIPATION_ALLOWED_PRODUCT_IDS.some(id => id === productId || relatedProductIds.includes(id));
  }, [connectionData]);

  const {
    data: participationsData,
    isLoading: isLoadingParticipations,
    isFetching: isFetchingParticipations,
    refetch: refetchParticipations,
  } = useEmployeeGetParticipationsQuery({
    variables: { connectionId, employeeId },
    options: { enabled: !!(connectionId && employeeId && showParticipations) },
  });

  const allParticipations = React.useMemo(() => participationsData ?? [], [participationsData]);
  const wiaParticipations = React.useMemo(
    () => allParticipations.filter(p => p.type === ParticipationTypeEnum.WIAExcedent),
    [allParticipations],
  );

  const wgaParticipations = React.useMemo(
    () => allParticipations.filter(p => p.type !== ParticipationTypeEnum.WIAExcedent),
    [allParticipations],
  );
  const isParticipationsLoading = isLoadingParticipations || isFetchingParticipations;

  const isLoading = isEmployeeCalculationsLoading;
  const isRegularConnection = data?.isRegular ?? true;

  const onFilterChange = React.useCallback(({ name, value }: { name: string; value: number | string }) => {
    if (name === 'date' && typeof value === 'string') {
      const isValidDate = Yup.date().isValidSync(value);
      isValidDate && setDateFilterValue(value);
    }
  }, []);

  const FilterFields = React.useMemo(() => {
    return [
      {
        type: 'date',
        name: 'date',
        header: TranslationKeys.employees_pension_referenceDate,
        defaultValue: defaultDateFilterValue,
        loading: isLoading,
        debounceTime: ONE_SECOND_DEBOUNCE_TIME,
      },
    ].map(field => (
      <Grid key={field.name} item xs={12}>
        <InputFieldFactory field={field} onChange={onFilterChange} />
      </Grid>
    ));
  }, [defaultDateFilterValue, isLoading, onFilterChange]);

  return (
    <Fragment>
      {!isRegularConnection && <EmployeePensionNotRegularMsg />}

      {isRegularConnection && (
        <Grid container spacing={2} wrap={'wrap-reverse'}>
          <Grid item xs={12} md={10}>
            <Grid container spacing={2} mb={5}>
              <Grid item container xs={12} md={6}>
                <EmployeePensionPersonalDetailsPanel loading={isLoading} data={data?.personal} />
              </Grid>
              <Grid item container xs={12} md={6}>
                <EmployeePensionIncomeDetailsPanel
                  loading={isLoading}
                  titleId={TranslationKeys.employees_detail_wagesIncome}
                  data={data?.income}
                />
              </Grid>
              <Grid item container xs={12} md={6}>
                <EmployeeIncomePremiumSickLeavePanel
                  data={data?.premiumSickLeave}
                  loading={isLoading}
                  onChangeCalculations={onChangeCalculations}
                />
              </Grid>
              <Grid item container xs={12} md={6}>
                <EmployeeIncomePremiumsWIA
                  data={data?.premiumWiaExcedentIvaExcedent}
                  loading={isLoading}
                  onChangeCalculations={onChangeCalculations}
                  connectionId={connectionId}
                  employeeId={employeeId}
                  participations={showParticipations ? wiaParticipations : undefined}
                  participationsLoading={showParticipations ? isParticipationsLoading : false}
                  refetchParticipations={showParticipations ? refetchParticipations : undefined}
                />
              </Grid>
              <Grid item container xs={12} md={6}>
                <EmployeeIncomePremiumsWGA
                  data={data?.premiumWgaGatWiaBodem}
                  loading={isLoading}
                  onChangeCalculations={onChangeCalculations}
                  connectionId={connectionId}
                  employeeId={employeeId}
                  participations={showParticipations ? wgaParticipations : undefined}
                  participationsLoading={showParticipations ? isParticipationsLoading : false}
                  refetchParticipations={showParticipations ? refetchParticipations : undefined}
                />
              </Grid>
              <Grid item container xs={12} md={6}>
                <EmployeeIncomePremiumsWGAERD
                  data={data?.premiumWgaErd}
                  loading={isLoading}
                  onChangeCalculations={onChangeCalculations}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={2}>
            <Grid container spacing={2}>
              {FilterFields}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Fragment>
  );
};
